/**
 * MinorCounters Component
 * 
 * Displays minor prizes as simple counters with +/- controls for efficient tracking.
 * Minor prizes are tracked by numeric count rather than individual badges.
 * 
 * @module TabPicker.MinorCounters
 */

import React from 'react';
import { useComparisonStore } from '../stores/comparisonStore';
import { cn } from '../utils/cn';

/**
 * Individual minor prize counter component
 */
const MinorPrizeCounter: React.FC<{
  gameId: number;
  prizeAmount: string;
  totalQuantity: number;
  claimedCount: number;
  onUpdate: (newCount: number) => void;
}> = ({ prizeAmount, totalQuantity, claimedCount, onUpdate }) => {
  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: num % 1 === 0 ? 0 : 2,
    }).format(num);
  };

  const handleIncrement = () => {
    if (claimedCount < totalQuantity) {
      onUpdate(claimedCount + 1);
    }
  };

  const handleDecrement = () => {
    if (claimedCount > 0) {
      onUpdate(claimedCount - 1);
    }
  };

  const handleDirectInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= totalQuantity) {
      onUpdate(value);
    }
  };

  const remaining = totalQuantity - claimedCount;
  const progressPercentage = totalQuantity > 0 ? (claimedCount / totalQuantity) * 100 : 0;

  return (
    <div className="p-3 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">
            {formatCurrency(prizeAmount)}
          </span>
          <span className="text-sm text-gray-500">
            ({remaining} remaining)
          </span>
        </div>
        <span className="text-xs text-gray-400">
          Total: {totalQuantity}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div 
          className={cn(
            "h-2 rounded-full transition-all duration-300",
            progressPercentage === 100 ? "bg-red-500" : 
            progressPercentage > 75 ? "bg-orange-500" : 
            progressPercentage > 0 ? "bg-yellow-500" : "bg-green-500"
          )}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Counter controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleDecrement}
          disabled={claimedCount <= 0}
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center text-lg font-medium transition-colors",
            claimedCount <= 0
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-red-100 text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
          )}
          aria-label={`Decrease ${formatCurrency(prizeAmount)} count`}
        >
          −
        </button>

        <div className="flex flex-col items-center">
          <input
            type="number"
            min="0"
            max={totalQuantity}
            value={claimedCount}
            onChange={handleDirectInput}
            className="w-16 px-2 py-1 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            aria-label={`${formatCurrency(prizeAmount)} claimed count`}
          />
          <span className="text-xs text-gray-500 mt-1">claimed</span>
        </div>

        <button
          onClick={handleIncrement}
          disabled={claimedCount >= totalQuantity}
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center text-lg font-medium transition-colors",
            claimedCount >= totalQuantity
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-green-100 text-green-700 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
          )}
          aria-label={`Increase ${formatCurrency(prizeAmount)} count`}
        >
          +
        </button>
      </div>
    </div>
  );
};

/**
 * Main MinorCounters component
 */
export const MinorCounters: React.FC<{
  gameId: number;
  minorPrizes: Array<{
    amount: string;
    quantity: number;
  }>;
}> = ({ gameId, minorPrizes }) => {
  const { config, updateMinorPrizeClaim } = useComparisonStore();
  
  // Get deal status for this game
  const dealStatus = config?.dealStatuses?.find?.(ds => ds?.gameId === gameId);
  const minorPrizeClaims = dealStatus?.minorPrizeClaims || {};

  // Sort minor prizes by amount (descending)
  const sortedMinorPrizes = [...minorPrizes].sort((a, b) => {
    return parseFloat(b.amount) - parseFloat(a.amount);
  });

  const handleUpdateClaim = (prizeAmount: string, newCount: number) => {
    updateMinorPrizeClaim(gameId, prizeAmount, newCount);
  };

  if (minorPrizes.length === 0) {
    return (
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-500 italic">
          No minor prizes configured for this game
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortedMinorPrizes.map((prize) => {
        const claimedCount = minorPrizeClaims[prize.amount] || 0;
        
        return (
          <MinorPrizeCounter
            key={prize.amount}
            gameId={gameId}
            prizeAmount={prize.amount}
            totalQuantity={prize.quantity}
            claimedCount={claimedCount}
            onUpdate={(newCount) => handleUpdateClaim(prize.amount, newCount)}
          />
        );
      })}
    </div>
  );
};